import React, { useState, useEffect } from "react";
import SEO from "../seo";
const SearchForApps = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "browse-and-search",
        "set-language",
        "app-recommendations",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Search for Apps | xPage Help Center"
        description="Learn how to search, browse, and discover apps in the Xpage App Store. Set your preferred language and receive personalized app recommendations to grow your online store."
        keywords="Xpage apps, search for apps, app store, browse apps, app recommendations, ecommerce apps"
        canonical="https://help.xpage.ai/help/apps/Search%20for%20Apps"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Search for Apps 
          </h1>
          <p className="text-sm text-systemLabel">
            The Xpage App Store offers a wide range of apps designed to enhance
            store functionality, meet your business needs, and drive growth.
          </p>
        </header>

        {/* Browse and Search Section */}
        <section id="browse-and-search" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Browse and Search
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                You can discover apps in several ways:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Search Bar
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Enter the app name to quickly locate what you need. For
                      best results, use English keywords, as most app
                      descriptions are in English.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Browse by Category
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Use the dropdown menu to explore apps by category, such as
                      Marketing & Conversion or Product & Sales Tools, to find
                      solutions tailored to specific business needs.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Set Language Section */}
        <section id="set-language" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Set Language
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Choose your preferred language from the menu in the App Store to
                improve your browsing experience.
              </p>

              <p className="text-sm text-systemLabel">
                Navigation and category labels will update to the selected
                language.
              </p>

              <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
                <p className="text-sm text-systemLabel">
                  Note: Most app descriptions are currently available in English
                  only.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* App Recommendations Section */}
        <section id="app-recommendations" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            App Recommendations
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Xpage provides personalized app recommendations to help you run
                and grow your store more effectively. Recommendations are based
                on:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Similar Store Patterns
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Preferences and usage patterns of stores similar to yours
                      (industry, location, and store attributes).
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Your Store Behavior
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Your Xpage usage and store behavior.
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="text-sm text-systemLabel">
                You can find recommended apps in:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Apps Page
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      The Apps page in your Xpage dashboard.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        App Store Homepage
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      The "Popular Recommendations for Similar Stores" section
                      on the App Store homepage.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Logout Page
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      The recommended section on the logout page.
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
                <p className="text-sm text-systemLabel">
                  Note: Recommendations are data-driven suggestions, not
                  advertisements, aimed at helping you find the apps that best
                  support your business growth.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#browse-and-search"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "browse-and-search"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Browse and Search
              </a>
            </li>
            <li>
              <a
                href="#set-language"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "set-language"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Set Language
              </a>
            </li>
            <li>
              <a
                href="#app-recommendations"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "app-recommendations"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                App Recommendations
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SearchForApps;
